import type { ConditionRow } from './policyFormUtils';
import type { AgentPolicyCondition } from '@repo/api/schemas/agentPolicy.schema';
import type { MenuOption } from '@saviynt/design-system';

/**
 * Condition form helpers used by ConditionContent.
 * - isRowIncomplete: validate row for "Incomplete condition" message
 * - mapApiConditionsToRows: initial state + edit prefill from API
 * - buildConditionMenuOptions: condition (field) dropdown options
 */

/** Static condition (field) options shown on top, in addition to filter-options API response */
export const STATIC_CONDITION_OPTIONS: MenuOption[] = [
  { value: 'name', label: 'name' },
  { value: 'description', label: 'description' },
  { value: 'platform', label: 'platform' },
  { value: 'llm_model', label: 'llm_model' },
  { value: 'criticality', label: 'criticality' },
];

/** API condition item shape (object form only) */
interface ApiConditionObject {
  field: string;
  operator: string;
  value: string;
}

function isConditionObject(c: unknown): c is ApiConditionObject {
  return (
    typeof c === 'object' &&
    c !== null &&
    'field' in c &&
    'operator' in c &&
    'value' in c
  );
}

/** True if row has any empty required field (attribute, operator, value) */
export function isRowIncomplete(row: ConditionRow): boolean {
  const a = (row.attribute ?? '').trim();
  const o = (row.operator ?? '').trim();
  const v = (row.value ?? '').trim();
  return a === '' || o === '' || v === '';
}

/** Map API conditions to form rows. Returns [] when none. */
export function mapApiConditionsToRows(
  apiConditions: AgentPolicyCondition | undefined
): ConditionRow[] {
  if (!apiConditions?.conditions?.length) {
    return [];
  }
  return apiConditions.conditions.filter(isConditionObject).map((c, idx) => ({
    id: `edit-${idx}-${c.field}-${c.operator}`,
    attribute: c.field,
    operator: c.operator,
    value: String(c.value ?? ''),
  }));
}

/** Build MenuOption[] for the condition (field) dropdown from API, current rows, search, and known values. */
export function buildConditionMenuOptions(
  previousConditions: AgentPolicyCondition | undefined,
  conditions: ConditionRow[],
  apiConditionOptions: { label?: string; value?: string }[],
  conditionSearchTerm: string,
  knownValues: Set<string>
): MenuOption[] {
  const opts: MenuOption[] = [];
  const seen = new Set<string>();

  const add = (label: string, value: string) => {
    const v = value.trim();
    if (!v || seen.has(v)) {
      return;
    }
    seen.add(v);
    opts.push({ label: label || v, value: v });
  };

  // Static options first (name, description, platform, etc.)
  STATIC_CONDITION_OPTIONS.forEach((o) => add(o.label, o.value));

  previousConditions?.conditions?.forEach((c) => {
    if (isConditionObject(c) && c.field?.trim()) {
      add(c.field.trim(), c.field.trim());
    }
  });
  conditions.forEach((c) => {
    if (c.attribute?.trim()) {
      add(c.attribute.trim(), c.attribute.trim());
    }
  });
  apiConditionOptions.forEach((o) => {
    if (o.value) {
      add((o.label ?? o.value) as string, o.value);
    }
  });
  const trimmed = conditionSearchTerm.trim();
  if (trimmed) {
    add(trimmed, trimmed);
  }
  knownValues.forEach((value) => {
    if (value && !seen.has(value)) {
      opts.push({ label: value, value });
    }
  });

  return opts;
}
